/**
 * Settings, and the installation's own health.
 *
 * The health panel is the reason this page matters operationally. It surfaces
 * `notifications.redirected` prominently, because that flag being left on is
 * the single most expensive configuration mistake in a self-hosted deployment:
 * every email and text goes to a test recipient instead of the person it names,
 * silently, and the failure is invisible from inside the application.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive,
  Check,
  Database,
  HardDrive,
  HeartPulse,
  Info,
  Layers,
  LifeBuoy,
  Mail,
  Palette,
  Server,
  X,
} from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Card,
  CardBody,
  CardHeader,
  DetailField,
  DetailGrid,
  ErrorState,
  LoadingState,
  PageHeader,
  Sections,
  api,
  buttonClass,
  formatDateTime,
  useTheme,
} from '@connect/web-shared';

import { EmailPreviews } from './email-previews';

interface HealthDetail {
  app: string;
  organization: string;
  environment: string;
  version: string;
  dependencies: { database: string; redis: string };
  notifications: {
    redirected: boolean;
    test_recipient_email: boolean;
    test_recipient_phone: boolean;
  };
  storage: { backend: string };
  background: { celery_enabled: boolean };
  /** What the server's backup script last reported (API `app.db.backup_status`). */
  backup?: {
    last_ok_at: string | null;
    last_ok_file: string | null;
    /** Whether that backup was also copied off the server; null from an older script. */
    last_ok_offsite?: boolean | null;
    last_failed_at: string | null;
    last_failed_step: string | null;
  };
  services: string[];
  notices: string[];
}

/** The last verified backup, or what stopped the one after it. */
export function LastBackup({ backup }: { backup: NonNullable<HealthDetail['backup']> }) {
  // Compared as instants the server stamped, both in UTC — not as calendar days.
  const failedSince =
    backup.last_failed_at !== null &&
    (backup.last_ok_at === null || Date.parse(backup.last_failed_at) > Date.parse(backup.last_ok_at));
  return (
    <span className="flex items-start gap-1.5">
      <Archive className="mt-0.5 size-3 shrink-0 text-faint" aria-hidden />
      <span>
        {backup.last_ok_at ? (
          <>
            {formatDateTime(backup.last_ok_at)}
            <span className="block font-mono text-2xs text-muted">{backup.last_ok_file}</span>
            {backup.last_ok_offsite === true ? (
              <span className="block text-2xs text-muted">Copied off the server</span>
            ) : backup.last_ok_offsite === false ? (
              <span className="block text-2xs text-warning">Kept on this server only</span>
            ) : null}
          </>
        ) : (
          <span className="text-danger">None reported</span>
        )}
        {failedSince ? (
          <span className="block text-2xs text-danger">
            Failed at {backup.last_failed_step} on {formatDateTime(backup.last_failed_at)}
          </span>
        ) : null}
      </span>
    </span>
  );
}

function Dependency({ label, state, icon }: { label: string; state: string; icon: React.ReactNode }) {
  const ok = state === 'ok';
  return (
    <div className="flex items-center gap-2.5 rounded-control border border-line px-3 py-2">
      <span className={ok ? 'text-success' : 'text-danger'}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-medium text-body">{label}</span>
        <span className={`block text-2xs ${ok ? 'text-muted' : 'text-danger'}`}>
          {ok ? 'Reachable' : 'Unreachable'}
        </span>
      </span>
      {ok ? (
        <Check className="size-3.5 text-success" aria-hidden />
      ) : (
        <X className="size-3.5 text-danger" aria-hidden />
      )}
    </div>
  );
}

export function SettingsPage() {
  const { preference, setMode } = useTheme();

  const health = useQuery({
    queryKey: ['health', 'detail'],
    // Under the versioned prefix like everything else a client calls, and
    // permission-gated: an unauthenticated endpoint listing versions and
    // reachable dependencies is reconnaissance. `/health/live` and
    // `/health/ready` stay unversioned for the orchestrator's probes.
    queryFn: () => api.get<HealthDetail>('/health/detail'),
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Settings"
        description="This installation, and how it is configured."
      />

      {/* Surfaced first and loudly. See the header. */}
      {health.data?.notifications.redirected ? (
        <Alert tone="danger" title="Outbound messages are being diverted to a test recipient">
          <div className="space-y-1.5">
            <p>
              Every email{health.data.notifications.test_recipient_phone ? ' and text' : ''} is
              going to the configured test address instead of to the person it names. That is
              correct while the data in this installation is invented, and a total and silent
              failure of communication once it is not.
            </p>
            <p className="text-xs">
              Clear <code className="font-mono">TEMP_MAIL</code>
              {health.data.notifications.test_recipient_phone ? (
                <>
                  {' '}
                  and <code className="font-mono">TEMP_PHONE</code>
                </>
              ) : null}{' '}
              in the deployment environment and restart the API. Both — forgetting one is the
              likely mistake and the one that looks finished.
            </p>
          </div>
        </Alert>
      ) : null}

      {health.data?.notices && health.data.notices.length > 0 ? (
        <Alert tone="warning" title="Worth knowing about this installation">
          <ul className="list-inside list-disc space-y-0.5 text-xs">
            {health.data.notices.map((notice) => (
              <li key={notice}>{notice}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <Sections
        label="Settings"
        items={[
          {
            id: 'services',
            label: 'Services',
            hint: 'What this installation offers',
            icon: <Layers />,
            content: (
              <Card>
                <CardHeader
                  title="Services"
                  description="What this installation is made of."
                  actions={
                    <Link
                      to="/settings/services"
                      className={buttonClass({ variant: 'secondary', size: 'sm' })}
                    >
                      <LifeBuoy className="size-3.5" />
                      Manage
                    </Link>
                  }
                />
                <CardBody>
                  <div className="flex flex-wrap gap-1.5">
                    {(health.data?.services ?? []).map((service) => (
                      <Badge key={service} tone="accent">
                        {service}
                      </Badge>
                    ))}
                  </div>
                </CardBody>
              </Card>
            ),
          },
          {
            id: 'health',
            label: 'Installation health',
            hint: 'Database, mail, texts and more',
            icon: <HeartPulse />,
            content: (
              <Card>
                <CardHeader
                  title="Installation health"
                  description="Refreshed every minute."
                  actions={
                    health.data ? (
                      <Badge tone={health.data.environment === 'production' ? 'success' : 'warning'}>
                        {health.data.environment}
                      </Badge>
                    ) : null
                  }
                />
                <CardBody>
                  {health.isLoading ? (
                    <LoadingState label="Reading the installation state" />
                  ) : health.isError ? (
                    <ErrorState
                      message={
                        health.error instanceof ApiError
                          ? health.error.message
                          : 'The health endpoint could not be read.'
                      }
                      onRetry={() => void health.refetch()}
                    />
                  ) : health.data ? (
                    <div className="space-y-4">
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Dependency
                          label="Database"
                          state={health.data.dependencies.database}
                          icon={<Database className="size-4" />}
                        />
                        <Dependency
                          label="Redis"
                          state={health.data.dependencies.redis}
                          icon={<Server className="size-4" />}
                        />
                      </div>

                      {health.data.dependencies.redis !== 'ok' ? (
                        <p className="flex items-start gap-1.5 rounded-control bg-warning-subtle px-2.5 py-1.5 text-2xs leading-relaxed text-warning">
                          <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
                          {/* Stated because it is a design decision, not a degradation
                              somebody should page about. */}
                          Redis is unreachable. Connect keeps working: rate limiting fails open and caches
                          fall through to the database, because a cache outage must not become an
                          authentication outage.
                        </p>
                      ) : null}

                      <DetailGrid>
                        <DetailField label="Organization">{health.data.organization}</DetailField>
                        <DetailField label="Version">
                          <span className="font-mono text-xs">{health.data.version}</span>
                        </DetailField>
                        <DetailField label="Document storage">
                          <span className="flex items-center gap-1.5">
                            <HardDrive className="size-3 text-faint" aria-hidden />
                            {health.data.storage.backend}
                          </span>
                        </DetailField>
                        <DetailField label="Background worker">
                          {health.data.background.celery_enabled ? 'Running' : 'Inline sends'}
                        </DetailField>
                        {health.data.backup ? (
                          <DetailField label="Last verified backup">
                            <LastBackup backup={health.data.backup} />
                          </DetailField>
                        ) : null}
                        <DetailField label="Outbound messages">
                          <span className="flex items-center gap-1.5">
                            <Mail className="size-3 text-faint" aria-hidden />
                            {health.data.notifications.redirected ? (
                              <span className="text-danger">Diverted to a test recipient</span>
                            ) : (
                              'Going to real recipients'
                            )}
                          </span>
                        </DetailField>
                      </DetailGrid>
                    </div>
                  ) : null}
                </CardBody>
              </Card>
            ),
          },
          {
            id: 'emails',
            label: 'Emails',
            hint: 'What Connect sends, as it is sent',
            icon: <Mail />,
            content: <EmailPreviews />,
          },
          {
            id: 'appearance',
            label: 'Appearance',
            hint: 'This browser only',
            icon: <Palette />,
            content: (
              <Card>
                <CardHeader title="Appearance" description="Applies to this browser only." />
                <CardBody className="space-y-3">
                  <div className="flex gap-1.5">
                    {(['light', 'dark', 'system'] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setMode(option)}
                        className={
                          preference === option
                            ? 'flex-1 rounded-control border border-accent bg-accent-subtle px-2 py-1.5 text-xs font-medium capitalize text-accent'
                            : 'flex-1 rounded-control border border-line px-2 py-1.5 text-xs capitalize text-muted hover:bg-surface-2'
                        }
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  <p className="text-2xs text-faint">
                    “System” follows your operating system and keeps following it, so it changes with
                    your machine at sunset rather than staying where you left it.
                  </p>
                </CardBody>
              </Card>
            ),
          },
          {
            id: 'about',
            label: 'About',
            hint: 'Version and build',
            icon: <Info />,
            content: (
              <Card>
                <CardHeader
                  title="About this installation"
                  description="Connect is self-hosted. Nothing here reaches anybody else, and no data leaves this deployment."
                />
                <CardBody>
                  <p className="text-xs leading-relaxed text-muted">
                    One installation serves one organization. There is no shared database, no
                    Connect-operated control plane, and no cross-customer anything — which is why there is
                    no tenant column in the schema and why data scope is enforced per role instead.
                  </p>
                </CardBody>
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
}
